import type { SearchAnswer, SearchQuery, SearchScope } from "./api";

/**
 * SRCH-2 and SRCH-3's vocabulary, in one place because two surfaces now ask the same
 * questions of the same endpoint.
 *
 * The desktop screen (M6.1, M8.1) and the phone app (M12.1) draw very different controls
 * over these — a filter row with popovers, and a scrolling strip of chips — but a search is
 * a search: the same filter state, the same omission rules, the same three scopes and the
 * same words for how the answer was ranked. Two copies of that would be two sets of copy to
 * keep level, and the first thing to drift would be which omission means "do not narrow".
 */

/** What the controls hold between searches. Sent as-is; the Server reads the omissions. */
export interface SearchFilters {
  scope: SearchScope;
  personId: string | null;
  entityId: string | null;
  /** ISO dates, on the source material's own date rather than when it was uploaded. */
  from: string | null;
  to: string | null;
  currentBeliefsOnly: boolean;
}

/**
 * The widest answer. `scope` is a placeholder here — a reset returns to the *account's*
 * default scope (`user_settings.default_search_scope`), never to a narrower one, so both
 * screens spread this and then override that one field.
 */
export const UNFILTERED: SearchFilters = {
  scope: "both",
  personId: null,
  entityId: null,
  from: null,
  to: null,
  currentBeliefsOnly: true,
};

/**
 * The request the Server reads: a null filter is left out entirely, because an omission is
 * what means "do not narrow by this".
 */
export function searchRequest(query: string, filters: SearchFilters): SearchQuery {
  return {
    query,
    scope: filters.scope,
    ...(filters.personId ? { personId: filters.personId } : {}),
    ...(filters.entityId ? { entityId: filters.entityId } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
    ...(filters.currentBeliefsOnly ? {} : { currentBeliefsOnly: false }),
  };
}

/** Whether anything is narrowing, given what a reset would return to. */
export function isNarrowed(filters: SearchFilters, unfiltered: SearchFilters): boolean {
  return (
    filters.personId !== null ||
    filters.entityId !== null ||
    filters.from !== null ||
    filters.to !== null ||
    !filters.currentBeliefsOnly ||
    filters.scope !== unfiltered.scope
  );
}

export const SEARCH_SCOPES: { id: SearchScope; label: string }[] = [
  { id: "world", label: "World view" },
  { id: "insights", label: "Insights" },
  { id: "both", label: "Both" },
];

/**
 * All three modes are ordinary states of a working system, so the one that ran is named
 * rather than hidden — an ordering nobody can account for is an ordering nobody trusts.
 */
export const SEARCH_MODE_LABEL: Record<SearchAnswer["mode"], string> = {
  "hybrid+rerank": "Text and meaning, re-ranked",
  hybrid: "Text and meaning",
  lexical: "Text only — no embedding model is provisioned yet",
};

/**
 * The presets, resolved against the day the page is on rather than against a stored date —
 * "the last 30 days" has to mean the last 30 days from now.
 */
export const DATE_PRESETS: { id: string; label: string; days: number | null }[] = [
  { id: "any", label: "Any time", days: null },
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
  { id: "365d", label: "Last 12 months", days: 365 },
];

export function isoDay(at: Date): string {
  return at.toISOString().slice(0, 10);
}

export function daysBack(days: number): string {
  const at = new Date();
  at.setDate(at.getDate() - days);
  return isoDay(at);
}
